import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ReporteParesDto } from '../dto/reporte-pares-dto';
import * as FileSaver from 'file-saver';
declare var require: any;
const ExcelJS: any = require('exceljs/dist/exceljs.bare.min.js');
const Workbook: any = ExcelJS;

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet; charset=UTF-8';
const EXCEL_EXT = '.xlsx';

@Injectable({
  providedIn: 'root'
})
export class ReporteParesService {

  url = `${environment.host}/api/reporte`;
  private logoUrl = 'assets/universidad-surcolombiana-multicampos-white.png';

  constructor(private http: HttpClient) { }

  obtenerReportePares(periodo: number, uaa: number): Observable<ReporteParesDto[]> {
    return this.http.get<ReporteParesDto[]>(`${this.url}/lista/pares/${periodo}/${uaa}`);
  }

  obtenerReporteObservaciones(periodo: number, uaa: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.url}/lista/observaciones/${periodo}/${uaa}`);
  }

  async exportToExcel(json: any[], excelFileName: string): Promise<void> {
    const workbook = new (ExcelJS.Workbook || ExcelJS)();
    const worksheet = workbook.addWorksheet('Pares Evaluadores');

    worksheet.columns = [
      { key: 'id_evaluado', width: 20 },
      { key: 'nombre_evaluado', width: 45 },
      { key: 'id_evaluador', width: 20 },
      { key: 'nombre_evaluador', width: 45 }
    ];

    await this.addHeader(worksheet, 'REPORTE DE PARES EVALUADORES', excelFileName);

    const headerRow = worksheet.getRow(7);
    headerRow.values = ['ID EVALUADO', 'NOMBRE EVALUADO', 'ID EVALUADOR', 'NOMBRE EVALUADOR'];
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF8F141B' }
      };
      cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' }
      };
    });

    json.forEach(item => {
      const row = worksheet.addRow({
        id_evaluado: item.identificacion_evaluado,
        nombre_evaluado: `${item.nombre_evaluado} ${item.apellido_evaluado}`,
        id_evaluador: item.identificacion_evaluador,
        nombre_evaluador: `${item.nombre_evaluador} ${item.apellido_evaluador}`
      });

      row.eachCell((cell) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });
    });

    this.saveWorkbook(workbook, 'REPORTE PARES ' + excelFileName);
  }

  async exportObservacionesToExcel(json: any[], excelFileName: string): Promise<void> {
    const workbook = new (ExcelJS.Workbook || ExcelJS)();
    const worksheet = workbook.addWorksheet('Observaciones');

    if (json.length > 0) {
      const keys = Object.keys(json[0]);
      const columns = keys.map(key => ({
        key: key,
        width: 30
      }));
      worksheet.columns = columns;

      await this.addHeader(worksheet, 'REPORTE DE OBSERVACIONES', excelFileName);

      const headerNames = keys.map(key => key.replace(/_/g, ' ').toUpperCase());

      const headerRow = worksheet.getRow(7);
      headerRow.values = headerNames;
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF8F141B' }
        };
        cell.font = { color: { argb: 'FFFFFFFF' }, bold: true };
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      json.forEach(item => {
        const row = worksheet.addRow(item);
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });
    }

    this.saveWorkbook(workbook, 'REPORTE OBSERVACIONES ' + excelFileName);
  }

  private async addHeader(worksheet: any, title: string, uaa: string) {
    const numCols = (worksheet.columns && worksheet.columns.length) ? worksheet.columns.length : 4;
    const lastColLetter = this.getColLetter(numCols);

    for (let i = 1; i <= 5; i++) {
      const row = worksheet.getRow(i);
      row.values = []; // Clear any automatic headers
      for (let j = 1; j <= numCols; j++) {
        const cell = row.getCell(j);
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF8F141B' }
        };
      }
    }

    try {
      const response = await fetch(this.logoUrl);
      const buffer = await response.arrayBuffer();
      const logoId = worksheet.workbook.addImage({
        buffer: buffer,
        extension: 'png',
      });
      worksheet.addImage(logoId, {
        tl: { col: 0.1, row: 0.2 },
        ext: { width: 320, height: 85 }
      });
    } catch (e) {
      console.warn('No se pudo cargar el logo para el Excel', e);
    }

    const startCol = numCols > 2 ? 2 : 1;
    worksheet.mergeCells(`${this.getColLetter(startCol)}2:${lastColLetter}2`);
    const titleCell = worksheet.getCell(`${this.getColLetter(startCol)}2`);
    titleCell.value = 'UNIVERSIDAD SURCOLOMBIANA';
    titleCell.font = { color: { argb: 'FFFFFFFF' }, size: 16, bold: true };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.mergeCells(`${this.getColLetter(startCol)}3:${lastColLetter}3`);
    const subtitleCell = worksheet.getCell(`${this.getColLetter(startCol)}3`);
    subtitleCell.value = title;
    subtitleCell.font = { color: { argb: 'FFFFFFFF' }, size: 14, bold: true };
    subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.mergeCells(`${this.getColLetter(startCol)}4:${lastColLetter}4`);
    const uaaCell = worksheet.getCell(`${this.getColLetter(startCol)}4`);
    uaaCell.value = `${uaa.toUpperCase()} | FECHA: ${new Date().toLocaleDateString()}`;
    uaaCell.font = { color: { argb: 'FFFFFFFF' }, size: 10 };
    uaaCell.alignment = { horizontal: 'center', vertical: 'middle' };

    worksheet.addRow([]);
  }

  private getColLetter(n: number): string {
    return String.fromCharCode(64 + n);
  }

  private async saveWorkbook(workbook: any, fileName: string) {
    const buffer = await workbook.xlsx.writeBuffer();
    const data: Blob = new Blob([buffer], { type: EXCEL_TYPE });
    const date = new Date();
    const formattedDate = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    FileSaver.saveAs(data, `${fileName}_${formattedDate}${EXCEL_EXT}`);
  }

}
